/**
 * Cookie consent.
 *
 * Nothing outside the "necessary" category may run until the visitor has said
 * yes to it. The GA4 tag used to load unconditionally from public/index.html,
 * which set _ga before anyone had clicked anything; it is now injected from
 * here only once analytics consent exists, and torn down when it is withdrawn.
 */

const CONSENT_COOKIE = 'cookie_consent';

// Bump when the categories or the vendors behind them change. A stored record
// carrying an older version is ignored, so the banner asks again instead of
// silently reusing consent the visitor never gave for the new processing.
//
// 2: `preferences` was added. A v1 record says nothing about it, and reading
//    silence as a yes is the exact assumption this check exists to prevent, so
//    v1 visitors are asked again.
const CONSENT_VERSION = 2;

const CONSENT_MAX_AGE_DAYS = 180;

const GA_MEASUREMENT_ID = 'G-1RSLLJGTCT';

/**
 * The categories the banner offers, in display order.
 *
 * Only what the site actually sets is listed: the consent record itself is
 * necessary, `language` is a preference, GA4 is analytics. Medidata runs no
 * advertising tech, so no such category is offered.
 *
 * `preferences` exists because esPublico's cookie policy classifies `language`
 * as "Funcional" and their banner text claims a personalisation purpose. That
 * only holds together if the visitor can actually refuse it — and they can,
 * because the language is carried in the url path (/pt/...). Refusing costs a
 * returning visitor their remembered language on the bare root, nothing more.
 */
export const CATEGORIES = [
  { key: 'necessary', required: true },
  { key: 'preferences', required: false },
  { key: 'analytics', required: false },
];

/** Set by useLang. Deleted when the preferences category is refused. */
const LANGUAGE_COOKIE = 'language';

export const OPTIONAL_CATEGORIES = CATEGORIES.filter((c) => !c.required);

const allOptional = (value) =>
  OPTIONAL_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: value }), {});

export const grantAll = () => allOptional(true);
export const denyAll = () => allOptional(false);

/* -------------------------------------------------------------------------- */
/* cookie plumbing                                                            */
/* -------------------------------------------------------------------------- */

const readRawCookie = (name) => {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const writeRawCookie = (name, value, maxAgeDays) => {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${Math.round(maxAgeDays * 24 * 60 * 60)}`,
    'samesite=lax',
  ];
  if (window.location.protocol === 'https:') parts.push('secure');
  document.cookie = parts.join('; ');
};

/**
 * Cookies set by a third-party script can carry a dot-prefixed domain, so
 * expiring them takes an attempt per candidate domain as well as per path.
 */
const deleteCookie = (name) => {
  const { hostname } = window.location;
  const domains = [undefined, hostname, `.${hostname}`];
  const bare = hostname.split('.').slice(-2).join('.');
  if (bare !== hostname) domains.push(bare, `.${bare}`);

  domains.forEach((domain) => {
    const parts = [`${name}=`, 'path=/', 'expires=Thu, 01 Jan 1970 00:00:00 GMT'];
    if (domain) parts.push(`domain=${domain}`);
    document.cookie = parts.join('; ');
  });
};

/* -------------------------------------------------------------------------- */
/* reading and writing the record                                             */
/* -------------------------------------------------------------------------- */

/**
 * @returns {{v: number, ts: string, granted: Object}|null} null when the
 * visitor has never answered, or answered a version of the question we no
 * longer ask — both of which mean "show the banner".
 */
export const readConsent = () => {
  const raw = readRawCookie(CONSENT_COOKIE);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION || typeof parsed.granted !== 'object') {
      return null;
    }
    return parsed;
  } catch (e) {
    // A record we cannot read is a record we cannot rely on.
    return null;
  }
};

export const writeConsent = (granted) => {
  const record = {
    v: CONSENT_VERSION,
    ts: new Date().toISOString(),
    // Normalise: never trust the caller to have covered every category, and
    // never store a value for a category that cannot be refused.
    granted: OPTIONAL_CATEGORIES.reduce(
      (acc, c) => ({ ...acc, [c.key]: granted[c.key] === true }),
      {}
    ),
  };
  writeRawCookie(CONSENT_COOKIE, JSON.stringify(record), CONSENT_MAX_AGE_DAYS);
  return record;
};

/**
 * The pre-consent banner stored `cookies=agree` with no way to refuse and no
 * categories. That is not consent under the RGPD, so it is not carried over:
 * the stale cookie is dropped and the visitor is asked properly.
 */
export const clearLegacyConsent = () => {
  if (readRawCookie('cookies') !== null) deleteCookie('cookies');
};

/* -------------------------------------------------------------------------- */
/* analytics                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * gtag.js reads the pushed value as an `arguments` object, not as an array, so
 * this has to stay a function declaration — an arrow function has no
 * `arguments` and the commands would be silently ignored.
 */
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

let analyticsScriptRequested = false;

const enableAnalytics = () => {
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  gtag('consent', 'update', { analytics_storage: 'granted' });

  // Configure once per page load. ModalCookies remounts whenever Main flips
  // back to its loading branch, and re-running config would bill a second
  // page_view for the same view.
  if (!analyticsScriptRequested) {
    analyticsScriptRequested = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });
  }
};

/**
 * Google cookie names are matched by pattern rather than listed.
 *
 * The measurement id in this file is not the only destination: a connected
 * site tag configured in the Google admin makes one gtag call write a second
 * `_ga_<id>` cookie for a property that appears nowhere in this codebase
 * (G-5P9ZBY5XH8, observed in the network trace). Any hardcoded list is
 * therefore already incomplete and would silently leave cookies behind after a
 * withdrawal, which is the one thing withdrawal must not do.
 */
const isAnalyticsCookie = (name) => /^_ga($|_)/.test(name) || /^_gid$/.test(name) || /^_gat/.test(name);

const disableAnalytics = () => {
  // Honoured by gtag.js even if the script is already in the page, which it
  // will be for anyone who granted and then withdrew in the same visit.
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  gtag('consent', 'update', { analytics_storage: 'denied' });

  document.cookie
    .split(';')
    .map((entry) => entry.trim().split('=')[0])
    .filter(isAnalyticsCookie)
    .forEach(deleteCookie);
};

/** Bring the page in line with a set of granted categories. */
export const applyConsent = (granted) => {
  if (granted && granted.analytics === true) {
    enableAnalytics();
  } else {
    disableAnalytics();
  }

  // useLang refuses to write it again while consent is absent, but one already
  // on the device has to go the moment it is refused - withdrawal that leaves
  // the cookie in place is not withdrawal.
  if (!granted || granted.preferences !== true) {
    deleteCookie(LANGUAGE_COOKIE);
  }
};

/**
 * Whether one optional category has been granted.
 *
 * For callers that set a cookie of their own and need to ask first. Absent or
 * unreadable record means no: the banner has not been answered yet, and the
 * default before an answer is always refusal.
 */
export const hasConsent = (key) => {
  const record = readConsent();
  return !!(record && record.granted && record.granted[key] === true);
};

/* -------------------------------------------------------------------------- */
/* reopening the panel from elsewhere                                         */
/* -------------------------------------------------------------------------- */

/**
 * Withdrawing consent has to be as easy as giving it, so the footer needs a way
 * to reopen the preferences panel. A module-level listener keeps that out of
 * the global store, which ModalCookies otherwise has no reason to touch.
 */
const listeners = new Set();

export const onOpenPreferences = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const openPreferences = () => {
  listeners.forEach((fn) => fn());
};
